import { oc } from "@orpc/contract";
import { z } from "zod";

export const PortalStatusSchema = z.enum(["draft", "published"]);

export const PortalCustomerSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

export const PortalSchema = z.object({
  id: z.uuid(),
  customerId: z.uuid(),
  name: z.string(),
  slug: z.string(),
  status: PortalStatusSchema,
  currentVersionId: z.uuid().nullable(),
  createdByUserId: z.uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  customer: PortalCustomerSchema.nullable(),
});

export const PortalVersionSchema = z.object({
  id: z.uuid(),
  portalId: z.uuid(),
  html: z.string().nullable(),
  jsonTree: z.unknown().nullable(),
  templateVariables: z.array(z.string()),
  createdByUserId: z.uuid().nullable(),
  createdAt: z.coerce.date(),
});

export const PortalUserStatusSchema = z.enum([
  "pending",
  "active",
  "deactivated",
]);

export const PortalUserSchema = z.object({
  id: z.uuid(),
  email: z.string().email(),
  name: z.string(),
  status: PortalUserStatusSchema,
  lastLoginAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
});

export const ListPortalsInputSchema = z.object({
  customerId: z.uuid().optional(),
  status: PortalStatusSchema.optional(),
  limit: z.int().min(1).max(100).prefault(20),
  cursor: z.string().optional(),
});

export const ListPortalsOutputSchema = z.object({
  portals: z.array(PortalSchema),
  nextCursor: z.string().nullable(),
});

export const GetPortalInputSchema = z.object({
  portalId: z.uuid(),
});

export const GetPortalOutputSchema = z.object({
  portal: PortalSchema.nullable(),
  versions: z.array(PortalVersionSchema),
});

export const UpdatePortalInputSchema = z.object({
  portalId: z.uuid(),
  name: z.string().min(1).max(255),
});

export const UpdatePortalOutputSchema = z.object({
  portal: PortalSchema,
});

export const PublishPortalInputSchema = z.object({
  portalId: z.uuid(),
});

export const PublishPortalOutputSchema = z.object({
  portal: PortalSchema,
});

export const UnpublishPortalInputSchema = z.object({
  portalId: z.uuid(),
});

export const UnpublishPortalOutputSchema = z.object({
  portal: PortalSchema,
});

export const RollbackPortalInputSchema = z.object({
  portalId: z.uuid(),
  versionId: z.uuid(),
});

export const RollbackPortalOutputSchema = z.object({
  currentVersionId: z.uuid(),
});

export const GetPortalPreviewInputSchema = z.object({
  portalId: z.uuid(),
  versionId: z.uuid().optional(),
  html: z.string().optional(),
});

export const GetPortalPreviewOutputSchema = z.object({
  html: z.string().nullable(),
  jsonTree: z.unknown().nullable(),
  sampleData: z
    .object({
      customer: z.object({ name: z.string() }),
      portalUser: z.object({ name: z.string() }),
      invoices: z.array(z.unknown()),
      orders: z.array(z.unknown()),
    })
    .nullable(),
});

export const ListPortalUsersInputSchema = z.object({
  portalId: z.uuid(),
});

export const ListPortalUsersOutputSchema = z.object({
  users: z.array(PortalUserSchema),
});

export const DeactivatePortalUserInputSchema = z.object({
  portalId: z.uuid(),
  portalUserId: z.uuid(),
});

export const DeactivatePortalUserOutputSchema = z.object({
  user: PortalUserSchema,
});

export const portalManagementContracts = {
  list: oc.input(ListPortalsInputSchema).output(ListPortalsOutputSchema).route({
    method: "GET",
  }),

  get: oc.input(GetPortalInputSchema).output(GetPortalOutputSchema).route({
    method: "GET",
  }),

  update: oc
    .input(UpdatePortalInputSchema)
    .output(UpdatePortalOutputSchema)
    .route({
      method: "POST",
    }),

  publish: oc
    .input(PublishPortalInputSchema)
    .output(PublishPortalOutputSchema)
    .route({
      method: "POST",
    }),

  unpublish: oc
    .input(UnpublishPortalInputSchema)
    .output(UnpublishPortalOutputSchema)
    .route({
      method: "POST",
    }),

  rollback: oc
    .input(RollbackPortalInputSchema)
    .output(RollbackPortalOutputSchema)
    .route({
      method: "POST",
    }),

  getPreview: oc
    .input(GetPortalPreviewInputSchema)
    .output(GetPortalPreviewOutputSchema)
    .route({
      method: "GET",
    }),

  listUsers: oc
    .input(ListPortalUsersInputSchema)
    .output(ListPortalUsersOutputSchema)
    .route({
      method: "GET",
    }),

  deactivateUser: oc
    .input(DeactivatePortalUserInputSchema)
    .output(DeactivatePortalUserOutputSchema)
    .route({
      method: "POST",
    }),
} as const;

export type PortalStatus = z.infer<typeof PortalStatusSchema>;
export type PortalCustomer = z.infer<typeof PortalCustomerSchema>;
export type Portal = z.infer<typeof PortalSchema>;
export type PortalVersion = z.infer<typeof PortalVersionSchema>;
export type PortalUserStatus = z.infer<typeof PortalUserStatusSchema>;
export type PortalUser = z.infer<typeof PortalUserSchema>;

export type ListPortalsInput = z.infer<typeof ListPortalsInputSchema>;
export type ListPortalsOutput = z.infer<typeof ListPortalsOutputSchema>;

export type GetPortalInput = z.infer<typeof GetPortalInputSchema>;
export type GetPortalOutput = z.infer<typeof GetPortalOutputSchema>;

export type UpdatePortalInput = z.infer<typeof UpdatePortalInputSchema>;
export type UpdatePortalOutput = z.infer<typeof UpdatePortalOutputSchema>;

export type PublishPortalInput = z.infer<typeof PublishPortalInputSchema>;
export type PublishPortalOutput = z.infer<typeof PublishPortalOutputSchema>;

export type UnpublishPortalInput = z.infer<typeof UnpublishPortalInputSchema>;
export type UnpublishPortalOutput = z.infer<typeof UnpublishPortalOutputSchema>;

export type RollbackPortalInput = z.infer<typeof RollbackPortalInputSchema>;
export type RollbackPortalOutput = z.infer<typeof RollbackPortalOutputSchema>;

export type GetPortalPreviewInput = z.infer<typeof GetPortalPreviewInputSchema>;
export type GetPortalPreviewOutput = z.infer<
  typeof GetPortalPreviewOutputSchema
>;

export type ListPortalUsersInput = z.infer<typeof ListPortalUsersInputSchema>;
export type ListPortalUsersOutput = z.infer<typeof ListPortalUsersOutputSchema>;

export type DeactivatePortalUserInput = z.infer<
  typeof DeactivatePortalUserInputSchema
>;
export type DeactivatePortalUserOutput = z.infer<
  typeof DeactivatePortalUserOutputSchema
>;

export const RegeneratePortalAsJsonInputSchema = z.object({
  portalId: z.uuid(),
});

export const RegeneratePortalAsJsonOutputSchema = z.object({
  portal: PortalSchema,
  newVersionId: z.uuid(),
});

export type RegeneratePortalAsJsonInput = z.infer<
  typeof RegeneratePortalAsJsonInputSchema
>;
export type RegeneratePortalAsJsonOutput = z.infer<
  typeof RegeneratePortalAsJsonOutputSchema
>;
